import type { Space } from '@cocoonspace/sdk-js/types/space'
import { ComponentProps, memo, useCallback, useMemo, useState } from 'react'
import { useMediaQuery } from '@cocoonspace/shared/hooks/use-media-query.hook'
import { getCleanObject } from '@cocoonspace/shared/utils/get-clean-object'
import { Button } from '@cocoonspace/ui/components/button'
import { cn } from '@cocoonspace/ui/lib/utils'
import { useLingui } from '@lingui/react/macro'
import { usePathname } from 'next/navigation'

import { mapEmitter } from '~/domains/search/emitters/search.emitters'
import { useSearchSpaces } from '~/domains/search/hooks/use-search-spaces.hook'
import { SpaceCard } from '../space-card'
import { SpacesGroupHeader } from './spaces-group-header'
import type { QueryFilters } from '../filters/use-search-filters.hook'

const ListWrapper = ({
	children,
	className,
	...props
}: ComponentProps<'div'>) => {
	return (
		<div
			className={cn(
				'mt-0.5 grid flex-1 gap-4 overflow-y-auto px-4',
				'group-data-[map-visible="true"]:lg:grid-cols-2',
				'group-data-[map-visible="false"]:md:grid-cols-2 group-data-[map-visible="false"]:lg:grid-cols-3 group-data-[map-visible="false"]:xl:grid-cols-4',
				className,
			)}
			{...props}
		>
			{children}
		</div>
	)
}

export const SpacesGroup = memo(
	({
		label,
		items,
		isAlternative = false,
		timeSlot,
	}: {
		label?: string
		items: Space[]
		timeSlot: {
			day: QueryFilters['day']
			start: QueryFilters['start']
			end: QueryFilters['end']
		}
		isAlternative?: boolean
	}) => {
		const pathname = usePathname() ?? ''
		const { t } = useLingui()
		const isTablet = useMediaQuery('(min-width: 640px)')
		const isDesktop = useMediaQuery('(min-width: 768px)')

		const {
			filters,
			sortBy,
			sortOrder,
			isMapVisible,
			activeAlternative,
			spacesListQuery,
		} = useSearchSpaces()

		const [showAll, setShowAll] = useState(() => !isAlternative)

		const alternativeColSize = useMemo(() => {
			if (isDesktop) return isMapVisible ? 2 : 3
			if (isTablet) return isMapVisible ? 1 : 2
			return 1
		}, [isDesktop, isTablet, isMapVisible])

		const spaces = useMemo(() => {
			return items
				?.filter((_, index) => {
					if (!isAlternative || showAll) return true

					return index < alternativeColSize
				})
				.sort((a, b) => {
					if (sortBy === 'capacity') {
						return sortOrder === 'asc'
							? a.capacity - b.capacity
							: b.capacity - a.capacity
					}

					return 0
				})
		}, [items, isAlternative, showAll, alternativeColSize, sortBy, sortOrder])

		const getSpaceHref = useCallback(
			(space: Space) => {
				const query = new URLSearchParams(
					getCleanObject({
						...(timeSlot.day && {
							day: timeSlot.day.join(','),
							end: timeSlot.end?.replace(':', '-'),
							start: timeSlot.start?.replace(':', '-'),
							step:
								timeSlot.start && timeSlot.end
									? 'timeSelect'
									: 'multiDaysSelect',
						}),
					}) as Record<string, string>,
				).toString()

				return `${pathname}/${space.slug}${query ? `?${query}` : ''}`
			},
			[pathname, timeSlot],
		)

		const onMouseEnter = useCallback((space: Space) => {
			mapEmitter.emit('map.cardOver', space)
		}, [])

		const onMouseLeave = useCallback(() => {
			mapEmitter.emit('map.cardOut')
		}, [])

		return (
			<div className='flex flex-col'>
				{(activeAlternative !== label || label) && (
					<SpacesGroupHeader
						isAlternative={isAlternative}
						items={items}
						label={label}
						timeSlot={timeSlot}
					/>
				)}

				<div className='min-h-0 flex-1 pb-2'>
					<ListWrapper className='py-[4px]'>
						{spaces.map((space) => (
							<SpaceCard
								key={space.id}
								id={space.id}
								data-testid={`space-${space.id}`}
								space={space}
								href={getSpaceHref(space)}
								className={cn('transition', {
									'opacity-70': spacesListQuery.isFetching,
								})}
								onMouseEnter={() => onMouseEnter(space)}
								onMouseLeave={() => onMouseLeave()}
								showDistance={!!filters?.text?.length}
								timeSlot={{
									day: timeSlot.day || [],
									start: timeSlot.start || undefined,
									end: timeSlot.end || undefined,
								}}
							/>
						))}
					</ListWrapper>

					{/* <VirtuosoGrid
					data-testid='spaces-layout-group'
					initialItemCount={Math.min(spaces.length, 8)}
					totalCount={spaces.length}
					className='h-full'
					components={{
						List: forwardRef<HTMLDivElement, GridListProps>(
							({ style, children }, listRef) => (
								<ListWrapper
									ref={listRef}
									style={style}
								>
									{children}
									<div className='col-span-full h-8' />
								</ListWrapper>
							),
						),
					}}
					itemContent={(index) => {
						const space = spaces[index]

						return (
							<SpaceCard
								key={space.id}
								id={space.id}
								data-testid={`space-${space.id}`}
								space={space}
								href={getSpaceHref(space)}
								className={cn('transition', {
									'opacity-70': spacesListQuery.isFetching,
								})}
								onMouseEnter={() => onMouseEnter(space)}
								onMouseLeave={() => onMouseLeave()}
								showDistance={!!filters?.text?.length}
							/>
						)
					}}
				/> */}
				</div>

				{isAlternative &&
					items?.length &&
					items?.length > alternativeColSize && (
						<div className='mt-8 w-full text-center'>
							<Button
								onClick={() => setShowAll((showAll) => !showAll)}
								variant='outline'
							>
								{showAll
									? t`Afficher moins de Cocoons`
									: t`Afficher les ${items?.length} Cocoons`}
							</Button>
						</div>
					)}
			</div>
		)
	},
)
